import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Sparkles, Mail, Loader2, ChevronLeft, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPassword() {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (response.ok) {
        setSent(true);
      } else {
        const data = await response.json();
        toast.error(data.detail || t('common.error'));
      }
    } catch {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-20 px-4 bg-background" data-testid="forgot-password-page">
      <div className="w-full max-w-md animate-fade-in-up">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <div className="w-10 h-10 rounded-xl bg-accent flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-accent-foreground" />
            </div>
            <span className="font-heading text-2xl font-bold">AFY</span>
          </Link>
          <h1 className="font-heading text-3xl font-semibold mb-2">{t('auth.forgotPassword')}</h1>
          <p className="text-muted-foreground">{t('auth.forgotPasswordSubtitle')}</p>
        </div>

        <Card className="border-border/50 shadow-soft">
          <CardContent className="pt-6">
            {sent ? (
              <div className="text-center py-4" data-testid="reset-email-sent">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                <h3 className="font-heading text-lg font-medium mb-2">{t('auth.resetEmailSent')}</h3>
                <p className="text-muted-foreground text-sm mb-6">{t('auth.resetEmailSentDesc')}</p>
                <Link to="/login">
                  <Button variant="outline" className="w-full" data-testid="back-to-login-btn">
                    <ChevronLeft className="w-4 h-4 mr-2" />
                    {t('auth.backToLogin')}
                  </Button>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">{t('auth.email')}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 h-12"
                      required
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="btn-primary w-full h-12"
                  disabled={loading}
                  data-testid="send-reset-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('common.loading')}
                    </>
                  ) : (
                    t('auth.sendResetLink')
                  )}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-muted-foreground mt-6">
          <Link to="/login" className="text-accent hover:underline font-medium" data-testid="back-login-link">
            <ChevronLeft className="w-4 h-4 inline mr-1" />
            {t('auth.backToLogin')}
          </Link>
        </p>
      </div>
    </div>
  );
}
